import React, { useState, useEffect } from 'react';
import { FiPlus } from 'react-icons/fi';
import './Configuracoes.css';

// Import components
import UserTable from './UserTable';
import UserModal from './UserModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import FilterTabs from './FilterTabs';
import SearchBox from './SearchBox';

// Import services
import * as UserService from './UserService';

const Configuracoes = () => {
  // State for users list
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // State for modal and form
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [currentUser, setCurrentUser] = useState(null);
  const [emailManuallyEdited, setEmailManuallyEdited] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    nomeCompleto: '',
    email: '',
    role: 'associado',
    password: '',
    confirmPassword: '',
    emailAlias: [] // Add email alias array
  });
  
  // State for all users (for email alias selection)
  const [allUsers, setAllUsers] = useState([]);
  
  // State for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  
  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab]);
  
  // Helper function to determine permissions and admin status from role
  const getRoleSettings = (role) => {
    switch(role) {
      case 'master':
        return { isAdmin: true, permissions: ['Master'] };
      case 'diretoria':
        return { isAdmin: true, permissions: ['Diretoria'] };
      case 'associado':
        return { isAdmin: false, permissions: ['Associado'] };
      case 'financeiro':
        return { isAdmin: false, permissions: ['Financeiro'] };
      case 'suporte':
        return { isAdmin: false, permissions: ['Suporte'] };
      default:
        return { isAdmin: false, permissions: [] };
    }
  };
  
  // Helper function to determine role from permissions
  const getRoleFromPermissions = (user) => {
    if (!user) return 'associado';
    
    if (user.permissions && user.permissions.includes('Master')) {
      return 'master';
    } else if (user.permissions && user.permissions.includes('Diretoria')) {
      return 'diretoria';
    } else if (user.permissions && user.permissions.includes('Associado')) {
      return 'associado';
    } else if (user.permissions && user.permissions.includes('Financeiro')) {
      return 'financeiro';
    } else if (user.permissions && user.permissions.includes('Suporte')) {
      return 'suporte';
    } else if (user.isAdmin) {
      return 'diretoria';
    } else {
      return 'associado';
    }
  };
  
  // Fetch users on component mount and when pagination changes
  useEffect(() => {
    fetchUsers();
    fetchAllUsers(); // Fetch all users for alias selection
  }, [currentPage, itemsPerPage]);
  
  // Fetch users from API with pagination
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await UserService.fetchUsers(currentPage, itemsPerPage);
      setUsers(response.users);
      setTotalPages(response.totalPages);
      setTotalUsers(response.totalUsers);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Erro ao carregar usuários. Por favor, tente novamente.');
      setUsers([]); // Initialize with empty array on error
      setTotalPages(1);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch all users for email alias selection
  const fetchAllUsers = async () => {
    try {
      // Fetch a large number of users for the alias selection
      const response = await UserService.fetchUsers(1, 1000); // Get up to 1000 users
      setAllUsers(response.users);
    } catch (err) {
      console.error('Error fetching all users:', err);
      setAllUsers([]);
    }
  };
  
  // Handle email alias checkbox changes
  const handleAliasChange = (userId) => {
    setFormData(prev => {
      const currentAliases = prev.emailAlias || [];
      const isCurrentlySelected = currentAliases.includes(userId);
      
      if (isCurrentlySelected) {
        // Remove from aliases
        return {
          ...prev,
          emailAlias: currentAliases.filter(id => id !== userId)
        };
      } else {
        // Add to aliases
        return {
          ...prev,
          emailAlias: [...currentAliases, userId]
        };
      }
    });
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle username change
    if (name === 'username') {
      // Only auto-generate email if it hasn't been manually edited
      if (!emailManuallyEdited) {
        setFormData(prev => ({
          ...prev,
          username: value,
          email: value ? `${value}@veridicus.com.br` : ''
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          username: value
        }));
      }
    } 
    // Handle email change (manual edit)
    else if (name === 'email') {
      setEmailManuallyEdited(true);
      setFormData(prev => ({
        ...prev,
        email: value
      }));
    } 
    // Handle other fields
    else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Open modal for adding new user
  const handleAddUser = () => {
    setModalMode('add');
    setEmailManuallyEdited(false);
    setFormData({
      username: '',
      nomeCompleto: '',
      email: '',
      role: 'associado',
      password: '',
      confirmPassword: '',
      emailAlias: []
    });
    setIsModalOpen(true);
  };
  
  // Open modal for editing user
  const handleEditUser = async (user) => {
    setModalMode('edit');
    setCurrentUser(user);
    
    // Get user's current email aliases
    let emailAliases = [];
    if (user.emailAlias && user.emailAlias.length > 0) {
      // If the user object already has populated emailAlias, extract the IDs
      emailAliases = user.emailAlias.map(alias => 
        typeof alias === 'string' ? alias : alias._id
      );
    }
    
    // Check if the email is different from the auto-generated one
    const autoGeneratedEmail = `${user.username}@veridicus.com.br`;
    const hasCustomEmail = user.email && user.email !== autoGeneratedEmail;
    setEmailManuallyEdited(hasCustomEmail);
    
    setFormData({
      username: user.username,
      nomeCompleto: user.nomeCompleto || '',
      email: user.email || autoGeneratedEmail,
      role: getRoleFromPermissions(user),
      password: '',
      confirmPassword: '',
      emailAlias: emailAliases
    });
    setIsModalOpen(true);
  };
  
  // Open delete confirmation modal
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };
  
  // Submit form for creating or updating user
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.username || !formData.email) {
      alert('Por favor, preencha todos os campos obrigatórios.');
      return;
    }
    
    if (modalMode === 'add' && (!formData.password || formData.password !== formData.confirmPassword)) {
      alert('As senhas não coincidem ou estão em branco.');
      return;
    }
    
    try {
      if (modalMode === 'add') {
        // Create new user
        const roleSettings = getRoleSettings(formData.role);
        const userData = {
          username: formData.username,
          nomeCompleto: formData.nomeCompleto,
          email: formData.email,
          password: formData.password,
          isAdmin: roleSettings.isAdmin,
          permissions: roleSettings.permissions,
          emailAlias: formData.emailAlias || []
        };
        
        await UserService.createUser(userData);
        
        // Refresh the user list to show the new user
        fetchUsers();
        
        // Close modal and reset form
        setIsModalOpen(false);
        setFormData({
          username: '',
          nomeCompleto: '',
          email: '',
          role: 'associado',
          password: '',
          confirmPassword: '',
          emailAlias: []
        });
        
        // Show success message
        alert('Usuário criado com sucesso!');
      } else {
        // Update existing user
        const roleSettings = getRoleSettings(formData.role);
        
        // Only send password if it was changed
        const updateData = {
          username: formData.username,
          nomeCompleto: formData.nomeCompleto,
          email: formData.email,
          isAdmin: roleSettings.isAdmin,
          permissions: roleSettings.permissions,
          emailAlias: formData.emailAlias || []
        };
        
        if (formData.password) {
          updateData.password = formData.password;
        }
        
        await UserService.updateUser(currentUser._id, updateData);
        
        // Refresh the user list to show the updated user
        fetchUsers();
        
        // Close modal and reset form
        setIsModalOpen(false);
        setFormData({
          username: '',
          nomeCompleto: '',
          email: '',
          role: 'associado',
          password: '',
          confirmPassword: '',
          emailAlias: []
        });
        
        // Show success message
        alert('Usuário atualizado com sucesso!');
      }
    } catch (err) {
      console.error('Error saving user:', err);
      alert('Erro ao salvar usuário. Por favor, tente novamente.');
    }
  };
  
  // Delete user
  const handleDeleteConfirm = async () => {
    try {
      await UserService.deleteUser(userToDelete._id);
      
      // Refresh the user list
      fetchUsers();
      
      // Close modal and reset state
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
      
      // Show success message
      alert('Usuário excluído com sucesso!');
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Erro ao excluir usuário. Por favor, tente novamente.');
    }
  };
  
  // Helper function to check if user has specific permission
  const hasPermission = (user, permission) => {
    return user.permissions && user.permissions.includes(permission);
  };
  
  // Note: With pagination, filtering should ideally be done server-side
  // For now, we'll display all users from current page
  // In a production app, you'd send filter parameters to the backend
  const filteredUsers = Array.isArray(users) ? users.filter(user => {
    const matchesSearch = (
      (user.username && user.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.nomeCompleto && user.nomeCompleto.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    if (activeTab === 'all') {
      return matchesSearch;
    } else if (activeTab === 'master') {
      return matchesSearch && hasPermission(user, 'Master');
    } else if (activeTab === 'diretoria') {
      return matchesSearch && (hasPermission(user, 'Diretoria') || (user.isAdmin && !hasPermission(user, 'Master')));
    } else if (activeTab === 'associado') {
      return matchesSearch && hasPermission(user, 'Associado');
    } else if (activeTab === 'financeiro') {
      return matchesSearch && hasPermission(user, 'Financeiro');
    } else if (activeTab === 'suporte') {
      return matchesSearch && hasPermission(user, 'Suporte');
    } else {
      return matchesSearch;
    }
  }) : [];
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR').format(date);
  };
  
  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page when changing items per page
  };
  
  // Calculate pagination display
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalUsers);
  
  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page, last page, and pages around current
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };
  
  return (
    <div className="configuracoes-container">
      {/* Header */}
      <div className="configuracoes-header">
        <h1>Configurações</h1>
        <button
          className="configuracoes-btn configuracoes-btn-primary"
          onClick={handleAddUser}
        >
          <FiPlus style={{ marginRight: '8px' }} /> Novo Usuário
        </button>
      </div>
      
      {/* Users Card */}
      <div className="configuracoes-card">
        <div className="card-header">
          <h3>Gerenciamento de Usuários</h3>
          
          {/* Search box */}
          <SearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        </div>
        
        {/* Filter tabs */}
        <FilterTabs activeTab={activeTab} setActiveTab={setActiveTab} />
        
        {/* Users table */}
        <UserTable 
          users={filteredUsers} 
          loading={loading} 
          error={error} 
          handleEditUser={handleEditUser} 
          handleDeleteClick={handleDeleteClick}
          formatDate={formatDate}
        />
        
        {/* Pagination controls */}
        {!loading && !error && totalUsers > 0 && (
          <div className="pagination-container">
            <div className="pagination-info">
              <span>Mostrando {startIndex}-{endIndex} de {totalUsers} usuários</span>
              
              {/* Items per page selector */}
              <div className="items-per-page">
                <label>Itens por página:</label>
                <select 
                  value={itemsPerPage} 
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  className="items-per-page-select"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
            
            <div className="pagination-controls">
              {/* Previous button */}
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Anterior
              </button>
              
              {/* Page numbers */}
              <div className="page-numbers">
                {getPageNumbers().map((page, index) => (
                  page === '...' ? (
                    <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                  ) : (
                    <button
                      key={page}
                      className={`page-number ${currentPage === page ? 'active' : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  )
                ))}
              </div>
              
              {/* Next button */}
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Próximo
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Modals */}
      <UserModal 
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        modalMode={modalMode}
        formData={formData}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        allUsers={allUsers}
        currentUserId={currentUser?._id}
        handleAliasChange={handleAliasChange}
      />
      
      <DeleteConfirmationModal 
        isOpen={isDeleteModalOpen}
        closeModal={() => setIsDeleteModalOpen(false)}
        userToDelete={userToDelete}
        handleDeleteConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default Configuracoes;